import {
  convertServerResponsivePropertyToClientResponsiveProperty,
  getResponsiveStylingClassNames,
} from "@faire/retailer-visitor-shared/components/SDUI/__internal__/Styling/responsiveValues";
import { getWidthConstraintCSS } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/Styling/widthConstraint";
import { ComponentLoggingEventParams } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/types";
import * as styles from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Banner/__internal__/BannerTextContent/styles/SDUIBannerTextContent.css";
import { SDUIBannerCTARenderer } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Banner/__internal__/SDUIBannerCTARenderer";
import { SDUIButtonRenderer } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Button/SDUIButtonRenderer";
import { SDUITypographyRenderer } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Typography/SDUITypographyRenderer";
import { getCssTextAlign } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Typography/styles/textJustificationStyleUtils";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { cn } from "@faire/web--source/tools/cn";
import { IBannerComponent } from "@faire/web-api--source/indigofair/data/IBannerComponent";
// eslint-disable-next-line no-restricted-imports
import { assignInlineVars } from "@vanilla-extract/dynamic";
import React from "react";

export const SDUIBannerTextContent: React.FC<{
  className?: string;
  "data-test-id"?: string;
  bannerComponent: IBannerComponent;
  loggingData: ComponentLoggingEventParams;
}> = ({
  "data-test-id": dataTestId,
  className,
  bannerComponent,
  loggingData,
}) => {
  const { tagline, title, sub_title, description, button, style } =
    bannerComponent;

  const {
    description_width,
    responsive_description_width,
    header_width,
    responsive_header_width,
  } = style ?? {};

  const responsiveDescriptionWidth =
    convertServerResponsivePropertyToClientResponsiveProperty(
      description_width,
      getWidthConstraintCSS,
      responsive_description_width
    );

  const responsiveHeaderWidth =
    convertServerResponsivePropertyToClientResponsiveProperty(
      header_width,
      getWidthConstraintCSS,
      responsive_header_width
    );

  return (
    <Column
      className={cn(className, "relative")}
      data-test-id={dataTestId}
      justify="center"
    >
      {tagline ? (
        <>
          <SDUITypographyRenderer
            textElement={tagline}
            loggingData={loggingData}
            componentStyle={{
              alignSelf: getCssTextAlign(tagline.style?.text_justification),
            }}
          />
          <Spacer height={{ mobileAndAbove: "1x", tabletAndAbove: "2x" }} />
        </>
      ) : null}
      {title ? (
        <SDUITypographyRenderer
          className={cn(
            getResponsiveStylingClassNames(responsiveHeaderWidth, {
              mobileAndAbove: styles.mobileAndAboveTitleWidth,
              tabletAndAbove: styles.tabletAndAboveTitleWidth,
              desktopAndAbove: styles.desktopAndAboveTitleWidth,
            })
          )}
          componentStyle={{
            ...assignInlineVars({
              [styles.varMobileTitleWidth]:
                responsiveHeaderWidth.mobileAndAbove,
              [styles.varTabletTitleWidth]:
                responsiveHeaderWidth.tabletAndAbove,
              [styles.varDesktopTitleWidth]:
                responsiveHeaderWidth.desktopAndAbove,
            }),
            alignSelf: getCssTextAlign(title.style?.text_justification),
          }}
          textElement={title}
          loggingData={loggingData}
        />
      ) : null}
      {sub_title ? (
        <>
          <Spacer height="1x" />
          <SDUITypographyRenderer
            textElement={sub_title}
            loggingData={loggingData}
            componentStyle={{
              alignSelf: getCssTextAlign(sub_title.style?.text_justification),
            }}
          />
        </>
      ) : null}
      {description.length > 0 ? (
        <>
          <Spacer height={{ mobileAndAbove: "1x", tabletAndAbove: "2x" }} />
          {description.map((descriptionElement, index) => (
            <React.Fragment key={index}>
              {index > 0 ? <Spacer height="1/2x" /> : null}
              <SDUITypographyRenderer
                className={cn(
                  getResponsiveStylingClassNames(responsiveDescriptionWidth, {
                    mobileAndAbove: styles.mobileAndAboveDescriptionWidth,
                    tabletAndAbove: styles.tabletAndAboveDescriptionWidth,
                    desktopAndAbove: styles.desktopAndAboveDescriptionWidth,
                  })
                )}
                componentStyle={{
                  ...assignInlineVars({
                    [styles.varMobileDescriptionWidth]:
                      responsiveDescriptionWidth.mobileAndAbove,
                    [styles.varTabletDescriptionWidth]:
                      responsiveDescriptionWidth.tabletAndAbove,
                    [styles.varDesktopDescriptionWidth]:
                      responsiveDescriptionWidth.desktopAndAbove,
                  }),
                  alignSelf: getCssTextAlign(
                    descriptionElement.style?.text_justification
                  ),
                }}
                textElement={descriptionElement}
                loggingData={loggingData}
              />
            </React.Fragment>
          ))}
        </>
      ) : null}
      {button ? (
        <>
          <Spacer height="2x" />
          <SDUIButtonRenderer
            className={styles.bannerButton}
            buttonElement={button}
            loggingData={loggingData}
            fireLegacyOnClickEvent
          />
        </>
      ) : null}
      <SDUIBannerCTARenderer bannerComponent={bannerComponent} />
    </Column>
  );
};
